import { useEffect, useRef, type MouseEvent, type SyntheticEvent } from "react";

/**
 * ═══ LA MÉCANIQUE D'UN `<dialog>` NATIF, ÉCRITE UNE FOIS ═══
 *
 * Ouvrir avec `showModal()`, fermer, laisser Échap et le fond congédier. L'élément
 * natif apporte ce que les surcouches faites à la main ratent : le focus déplacé
 * puis retenu dedans, le reste de la page rendu inerte, un fond qui est un vrai
 * pseudo-élément plutôt qu'une div à empiler.
 *
 * ⚠️ EXTRAIT PARCE QU'IL Y EN AVAIT DÉJÀ DEUX COPIES. `Confirm` et `Modal`
 * portaient le même effet et les mêmes deux gestionnaires, à la classe CSS près —
 * et l'en-tête de `Modal` affirmait être le châssis de `Confirm`, ce qui était faux.
 * Deux copies d'une mécanique, c'est la seconde qui oublie Échap le jour où on
 * touche à la première. Leur MARKUP reste distinct : ce sont deux objets différents
 * (une question courte, une boîte à contenu), et les deux jeux de classes sont
 * dessinés séparément.
 */
export function useDialog(open: boolean, onDismiss: () => void) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  /**
   * ⚠️ FERMÉE AU DÉMONTAGE. Une boîte ouverte dont le composant disparaît laisse
   * derrière elle l'état modal du document : le reste de la page reste inerte et
   * le focus reste retenu là où il n'y a plus rien. L'élément est capté ici, à
   * l'ouverture de l'effet, parce que la référence est déjà vidée quand le nettoyage
   * s'exécute.
   */
  useEffect(() => {
    const el = ref.current;
    return () => {
      if (el?.open) el.close();
    };
  }, []);

  return {
    ref,
    /* Échap et le fond congédient tous les deux : une boîte qu'on ne peut pas
       quitter est un piège. */
    onCancel: (event: SyntheticEvent) => {
      event.preventDefault();
      onDismiss();
    },
    onClick: (event: MouseEvent<HTMLDialogElement>) => {
      if (event.target === ref.current) onDismiss();
    },
  };
}
